import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Badge, Empty, Tag } from 'antd';
import { SortAscendingOutlined } from '@ant-design/icons';

import { Spinner, Tooltip } from '../../../../common/index.jsx';
import {
  useGetAssignmentMatrixQuery,
  useManualAssignStudentMutation,
  useManualUnassignStudentMutation,
} from '../../../../../redux/services/ing_projects_assignments.jsx';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import './AssignmentMatrix.css';

const AssignmentMatrix = ({ studyProgramId, studentFilter, projectFilter, isPublished }) => {
  const { t } = useTranslation();
  const { successMessage, errorMessage } = useToastMessages();

  const [hoveredCol, setHoveredCol] = useState(null);
  const [studentSortMode, setStudentSortMode] = useState('average');

  const normalizeSlovakSortText = useCallback((value) => {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('sk');
  }, []);
  const headerRef = useRef(null);
  const bodyRef = useRef(null);
  const hoverFrameRef = useRef(null);

  const clearHoveredColumn = useCallback(() => {
    if (hoverFrameRef.current) {
      cancelAnimationFrame(hoverFrameRef.current);
      hoverFrameRef.current = null;
    }
    setHoveredCol(null);
  }, []);

  const updateHoveredColumn = useCallback((colIdx) => {
    if (hoverFrameRef.current) {
      cancelAnimationFrame(hoverFrameRef.current);
    }

    hoverFrameRef.current = requestAnimationFrame(() => {
      setHoveredCol((prevCol) => (prevCol === colIdx ? prevCol : colIdx));
      hoverFrameRef.current = null;
    });
  }, []);

  useEffect(() => () => {
    if (hoverFrameRef.current) {
      cancelAnimationFrame(hoverFrameRef.current);
    }
  }, []);

  const { data: response, isLoading } = useGetAssignmentMatrixQuery(
    { studyProgramId },
    { skip: !studyProgramId }
  );

  const [manualAssign, { isLoading: isAssigning }] = useManualAssignStudentMutation();
  const [manualUnassign, { isLoading: isUnassigning }] = useManualUnassignStudentMutation();

  const matrixData = response?.data || response;
  const formatAverageGrade = useCallback((value) => {
    return Number(value) === 5 ? '–' : value;
  }, []);

  const { applicationMap, assignmentMap, assignedCellSet } = useMemo(() => {
    if (!matrixData) return { applicationMap: {}, assignmentMap: {}, assignedCellSet: new Set() };

    const appMap = {};
    (matrixData.applications || []).forEach((app) => {
      appMap[`${app.student_id}-${app.ip_project_id}`] = {
        priority: app.priority,
        teacherPriority: app.teacher_priority,
      };
    });

    const assignMap = {};
    const cellSet = new Set();
    (matrixData.assignments || []).forEach((a) => {
      assignMap[a.student_id] = a.ip_project_id;
      cellSet.add(`${a.student_id}-${a.ip_project_id}`);
    });

    return { applicationMap: appMap, assignmentMap: assignMap, assignedCellSet: cellSet };
  }, [matrixData]);

  const filteredStudents = useMemo(() => {
    if (!matrixData?.students) return [];
    const students = matrixData.students.map((student) => ({
      ...student,
      is_locked: !!student.is_locked,
    }));

    const getLastNameForSort = (name) => {
      if (!name) return '';
      const parts = String(name).trim().split(/\s+/);
      return normalizeSlovakSortText(parts[0] || '');
    };
    const getAverageForSort = (value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
    };

    let filtered = students;
    if (studentFilter === 'assigned') {
      filtered = students.filter((student) => !!assignmentMap[student.student_id]);
    }
    if (studentFilter === 'unassigned') {
      filtered = students.filter((student) => !assignmentMap[student.student_id]);
    }

    return [...filtered].sort((left, right) => {
      if (studentSortMode === 'average') {
        const averageCompare = getAverageForSort(left.average_grade) - getAverageForSort(right.average_grade);
        if (averageCompare !== 0) {
          return averageCompare;
        }
      }

      const lastNameCompare = getLastNameForSort(left.name).localeCompare(getLastNameForSort(right.name), 'sk', {
        sensitivity: 'base',
      });

      if (lastNameCompare !== 0) {
        return lastNameCompare;
      }

      return normalizeSlovakSortText(left.name).localeCompare(normalizeSlovakSortText(right.name), 'sk', {
        sensitivity: 'base',
      });
    });
  }, [matrixData?.students, studentFilter, assignmentMap, studentSortMode, normalizeSlovakSortText]);

  const filteredProjects = useMemo(() => {
    if (!matrixData?.projects) return [];
    if (projectFilter === 'occupied') {
      return matrixData.projects.filter((p) => {
        const stats = matrixData.project_stats?.[p.id];
        return !!stats && stats.assigned > 0;
      });
    }
    if (projectFilter === 'empty') {
      return matrixData.projects.filter((p) => {
        const stats = matrixData.project_stats?.[p.id];
        return !stats || (stats.assigned || 0) === 0;
      });
    }
    return matrixData.projects;
  }, [matrixData?.projects, projectFilter, matrixData?.project_stats]);

  const hasAssignedInAnyProject = useMemo(() => matrixData?.projects?.some((p) => {
      const stats = matrixData.project_stats?.[p.id];
      return (stats?.assigned || 0) > 0;
    }), [matrixData?.projects, matrixData?.project_stats]);

  const projectNameById = useMemo(() => {
    if (!matrixData?.projects) return {};
    return Object.fromEntries(
      matrixData.projects.map((project) => [project.id, project.name]),
    );
  }, [matrixData?.projects]);

  const projectAssignments = useMemo(() => {
    if (!matrixData?.assignments) return {};
    const byProject = {};
    const tooltipAssignments = matrixData.project_assigned_students || matrixData.assignments || [];
    tooltipAssignments.forEach((assignment) => {
      const {
        student_id,
        ip_project_id,
        study_program_id,
        study_program_shortcut,
        student_name,
      } = assignment;

      byProject[ip_project_id] ??= [];
      byProject[ip_project_id].push({
        id: student_id,
        name: student_name || '–',
        isFromOtherStudyProgram: String(study_program_id) !== String(studyProgramId),
        studyProgramShortcut: study_program_shortcut || null,
      });
    });

    const getLastNameForSort = (name) => {
      const parts = String(name || '').trim().split(/\s+/);
      return normalizeSlovakSortText(parts[0] || '');
    };

    Object.keys(byProject).forEach((projectId) => {
      byProject[projectId].sort((a, b) => {
        const surnameCompare = getLastNameForSort(a.name).localeCompare(getLastNameForSort(b.name), 'sk', {
          sensitivity: 'base',
        });

        if (surnameCompare !== 0) {
          return surnameCompare;
        }

        return normalizeSlovakSortText(a.name).localeCompare(normalizeSlovakSortText(b.name), 'sk', {
          sensitivity: 'base',
        });
      });
    });

    return byProject;
  }, [matrixData?.assignments, matrixData?.project_assigned_students, studyProgramId, normalizeSlovakSortText]);

  const projectHasOtherProgramStudent = useMemo(() => {
    const result = {};
    Object.entries(projectAssignments).forEach(([projectId, students]) => {
      result[projectId] = students.some((student) => student.isFromOtherStudyProgram);
    });
    return result;
  }, [projectAssignments]);

  const handleCellClick = useCallback(async (studentId, projectId) => {
    if (isPublished) return;
    if (isAssigning || isUnassigning) return;

    const isAssigned = assignedCellSet.has(`${studentId}-${projectId}`);

    try {
      if (isAssigned) {
        await manualUnassign({ studentId, ipProjectId: projectId, studyProgramId }).unwrap();
        successMessage({ message: t('Priradenie zrušené') });
      } else {
        await manualAssign({ studentId, ipProjectId: projectId, studyProgramId }).unwrap();
        successMessage({ message: t('Študent priradený') });
      }
    } catch (error) {
      errorMessage({
        message: t('Operácia neúspešná'),
        description: error?.data?.message || t('Nastala chyba.'),
      });
    }
  }, [isPublished, isAssigning, isUnassigning, assignedCellSet, manualAssign, manualUnassign, studyProgramId, successMessage, errorMessage, t]);

  // Sync horizontal scroll from body to header
  const handleBodyScroll = useCallback((e) => {
    if (headerRef.current) {
      headerRef.current.scrollLeft = e.target.scrollLeft;
    }
  }, []);

  const renderProjectTooltip = useCallback((project) => {
    const assignedStudents = projectAssignments[project.id] || [];

    return (
      <div style={{ whiteSpace: 'normal', maxWidth: 420 }}>
        <div style={{ marginBottom: 6 }}><strong>{project.name}</strong></div>
        <div style={{ marginBottom: 8 }}>
          <strong>{t('Študijné programy')}</strong>: {project.study_programs || '–'}
        </div>
        <div style={{ marginBottom: 8 }}>
          <strong>{t('Garant')}</strong>: {project.guarantor_full_name || '–'}
        </div>
        <div style={{ marginBottom: 4 }}><strong>{t('Priradení študenti')}</strong>:</div>
        {assignedStudents.length > 0 ? (
          <div style={{ lineHeight: 1.6 }}>
            {assignedStudents.map((student) => (
              <div key={student.id} style={student.isFromOtherStudyProgram ? { fontStyle: 'italic' } : undefined}>
                {student.name}{student.studyProgramShortcut ? ` (${student.studyProgramShortcut})` : ''}
              </div>
            ))}
          </div>
        ) : (
          <div>–</div>
        )}
      </div>
    );
  }, [projectAssignments, t]);

  if (isLoading) return <Spinner />;

  if (!matrixData || !matrixData.projects?.length || !matrixData.students?.length) {
    return <Empty description={t('Žiadne dáta pre zvolený študijný program')} />;
  }

  return (
    <>
      {isPublished && (
        <Alert
          type="warning"
          showIcon
          title={t('Priradenie bolo zverejnené')}
          description={t('Maticu priradení nie je možné meniť. Zmena priradenia na projektoch môže byť vykonaná len na základe žiadosti študenta elektronickou formou.')}
          style={{ marginBottom: 12 }}
        />
      )}
      <div className="assignment-matrix-wrapper">
      {/* Sticky header - separate table synced horizontally */}
      <div className="assignment-matrix-header" ref={headerRef}>
        <table className="assignment-matrix">
          <thead>
            {/* Row 1: Project shortcuts (vertical) */}
            <tr>
              <td className="student-col"></td>
              <td className="avg-col"></td>
              {filteredProjects.map((p, colIdx) => {
                const hasAssigned = (matrixData.project_stats?.[p.id]?.assigned || 0) > 0;
                const hasOtherProgramStudent = !!projectHasOtherProgramStudent[p.id];

                return (
                  <td
                    key={p.id}
                    className={`header-col ${hoveredCol === colIdx ? 'col-hover' : ''} ${hasAssigned ? 'header-col-assigned' : ''} ${hasOtherProgramStudent ? 'header-col-foreign-assigned' : ''}`}
                    onMouseMove={() => updateHoveredColumn(colIdx)}
                    onMouseLeave={clearHoveredColumn}
                  >
                    <Tooltip tooltip={renderProjectTooltip(p)} maxWidth={520}>
                      <div className={`vertical-text ${hasAssigned ? 'vertical-text-assigned' : ''}`}>
                        {p.shortcut || p.name}
                      </div>
                    </Tooltip>
                  </td>
                );
              })}
            </tr>
            {/* Row 2: Guarantor (vertical) */}
            <tr>
              <td className="student-col"></td>
              <td className="avg-col"></td>
              {filteredProjects.map((p, colIdx) => {
                const hasAssigned = (matrixData.project_stats?.[p.id]?.assigned || 0) > 0;
                const hasOtherProgramStudent = !!projectHasOtherProgramStudent[p.id];

                return (
                  <td
                    key={p.id}
                    className={`header-col ${hoveredCol === colIdx ? 'col-hover' : ''} ${hasAssigned ? 'header-col-assigned' : ''} ${hasOtherProgramStudent ? 'header-col-foreign-assigned' : ''}`}
                    onMouseMove={() => updateHoveredColumn(colIdx)}
                    onMouseLeave={clearHoveredColumn}
                  >
                    <Tooltip tooltip={renderProjectTooltip(p)} maxWidth={520}>
                      <div className={`vertical-text ${hasAssigned ? 'vertical-text-assigned' : ''}`}>
                        {p.guarantor_last_name || '–'}
                      </div>
                    </Tooltip>
                  </td>
                );
              })}
            </tr>
            {/* Row 3: Stats - assigned/applicants + min-max */}
            <tr>
              <td className="student-col" style={{ fontWeight: 'bold' }}>
                <div className="student-header-content">
                  <span>{t('Študent')}</span>
                  <Tooltip
                    tooltip={
                      studentSortMode === 'average'
                        ? t('Zoradené podľa priemeru vzostupne. Kliknutím prepnete zoradenie podľa priezviska.')
                        : t('Zoradené podľa priezviska vzostupne. Kliknutím prepnete zoradenie podľa priemeru.')
                    }
                  >
                    <SortAscendingOutlined
                      className="student-sort-icon"
                      onClick={() => setStudentSortMode((prev) => (prev === 'average' ? 'surname' : 'average'))}
                    />
                  </Tooltip>
                </div>
              </td>
              <td className={`avg-col ${hasAssignedInAnyProject ? 'avg-col-assigned' : ''}`}>
                {t('Priemer')}
              </td>
              {filteredProjects.map((p, colIdx) => {
                const stats = matrixData.project_stats?.[p.id] || {};
                const hasAssigned = (stats.assigned || 0) > 0;
                const hasOtherProgramStudent = !!projectHasOtherProgramStudent[p.id];
                const statsTooltip = (
                  <div style={{ whiteSpace: 'normal' }}>
                    <div><strong>{t('Počet prihlásených')}</strong>: {stats.applicants || 0}</div>
                    <div><strong>{t('Počet priradených v tomto št. programe')}</strong>: {stats.assigned_in_study_program || 0}</div>
                    <div><strong>{t('Počet priradených v iných št. programoch')}</strong>: {stats.assigned_in_other_study_programs || 0}</div>
                    <div><strong>{t('Minimum študentov')}</strong>: {p.min_students}</div>
                    <div><strong>{t('Maximum študentov')}</strong>: {p.max_students}</div>
                  </div>
                );
                return (
                  <td
                    key={p.id}
                    className={`stats-col ${hoveredCol === colIdx ? 'col-hover' : ''} ${hasAssigned ? 'stats-col-assigned' : ''} ${hasOtherProgramStudent ? 'stats-col-foreign-assigned' : ''}`}
                    onMouseMove={() => updateHoveredColumn(colIdx)}
                    onMouseLeave={clearHoveredColumn}
                  >
                    <Tooltip tooltip={statsTooltip} maxWidth={320}>
                      <span className="stats-cell-content">
                        <b className={hasAssigned ? 'stats-value-assigned' : ''}>{stats.assigned || 0}/{stats.applicants || 0}</b>
                        {p.min_students}-{p.max_students}
                      </span>
                    </Tooltip>
                  </td>
                );
              })}
            </tr>
          </thead>
        </table>
      </div>

      {/* Scrollable body */}
      <div
        className="assignment-matrix-body"
        ref={bodyRef}
        onScroll={handleBodyScroll}
        onMouseLeave={clearHoveredColumn}
      >
        <table className="assignment-matrix">
          <tbody>
            {filteredStudents.map((student, index) => {
              const isStudentAssigned = !!assignmentMap[student.student_id];
              return (
                <tr key={student.student_id} className={isStudentAssigned ? 'row-assigned' : ''}>
                  <td className={`student-col ${isStudentAssigned ? 'assigned-student-meta' : ''}`}>
                    <Tooltip
                      tooltip={(
                        <div style={{ whiteSpace: 'normal' }}>
                          <div><strong>{t('Študent')}</strong>: {student.name}</div>
                          <div><strong>{t('Študijný priemer')}</strong>: {formatAverageGrade(student.average_grade)}</div>
                          <div>
                            <strong>{t('Priradený na projekt')}</strong>: {' '}
                            {assignmentMap[student.student_id]
                              ? (projectNameById[assignmentMap[student.student_id]] || '–')
                              : '–'}
                          </div>
                        </div>
                      )}
                    >
                      <span className="student-name-text">
                        <span className="student-name-label">
                          {index + 1}. {student.name}
                        </span>
                        {!student.is_locked ? (
                          <sup className="student-lock-indicator">●</sup>
                        ) : null}
                      </span>
                    </Tooltip>
                  </td>
                  <td className={`avg-col ${isStudentAssigned ? 'assigned-student-meta' : ''}`}>{formatAverageGrade(student.average_grade)}</td>
                  {filteredProjects.map((project, colIdx) => {
                    const cellKey = `${student.student_id}-${project.id}`;
                    const isAssigned = assignedCellSet.has(cellKey);
                    const app = applicationMap[cellKey];

                    let className = 'data-col';
                    let content = '';

                    if (isAssigned) {
                      className += ' cell-assigned';
                      content = app ? app.priority : '';
                      if (app?.teacherPriority) {
                        content = (
                          <>
                            {app.priority}
                            <span className="cell-teacher-priority">*</span>
                          </>
                        );
                      }
                    } else if (app) {
                      content = app.priority;
                      if (app.teacherPriority) {
                        content = (
                          <>
                            {app.priority}
                            <span className="cell-teacher-priority">*</span>
                          </>
                        );
                      }
                    }

                    if (hoveredCol === colIdx) {
                      className += ' col-hover';
                    }

                    const cellTooltip = (
                      <div style={{ whiteSpace: 'normal' }}>
                        <div><strong>{t('Študent')}</strong>: {student.name}</div>
                        <div><strong>{t('Študijný priemer')}</strong>: {formatAverageGrade(student.average_grade)}</div>
                        <div><strong>{t('Priorita')}</strong>: {app?.priority ?? '–'}</div>
                        <div>
                          <strong>{t('Preferencia garanta')}</strong>: {app?.teacherPriority ? t('Áno') : t('Nie')}
                        </div>
                        <div>
                          <strong>{t('Priradený na projekt')}</strong>: {' '}
                          {assignmentMap[student.student_id]
                            ? (projectNameById[assignmentMap[student.student_id]] || '–')
                            : '–'}
                        </div>
                        <div><strong>{t('Vyznačený projekt')}</strong>: {project.name}</div>
                      </div>
                    );

                    return (
                      <td
                        key={cellKey}
                        className={className}
                        onClick={() => handleCellClick(student.student_id, project.id)}
                        onMouseMove={() => updateHoveredColumn(colIdx)}
                        onMouseLeave={clearHoveredColumn}
                        style={isPublished ? { cursor: 'default', opacity: 0.8 } : undefined}
                      >
                        <Tooltip tooltip={cellTooltip} maxWidth={455}>
                          <span style={{ display: 'block', width: '100%', height: '100%' }}>
                            {content}
                          </span>
                        </Tooltip>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="assignment-matrix-legend">
        <div className="assignment-matrix-legend-item">
          <Tag style={{backgroundColor: "#dfe7f5"}}>1-6</Tag>
          <span>{t('Priorita študenta pre daný projekt')}</span>
        </div>
        <div className="assignment-matrix-legend-item">
          <Tag style={{backgroundColor: "#84dc14"}}><strong style={{color: "black"}}>{t('1-6')}</strong></Tag>
          <span>{t('Študent je priradený na projekt')}</span>
        </div>
        <div className="assignment-matrix-legend-item">
          <strong strong style={{color: "red"}}>*</strong>
          <span>{t('Garant projektu preferuje študenta na projekte')}</span>
        </div>
        <div className="assignment-matrix-legend-item">
          <sup style={{color: "red", fontSize: "10px", lineHeight: 1}}>●</sup>
          <span>{t('Študent nemá ukončený výber projektov')}</span>
        </div>
      </div>
      </div>
    </>
  );
};

export default AssignmentMatrix;
